// Plain teacher language and form rules for work set outside a lesson
// (Handoff 007 §9). The gateway is the authority: these checks only catch
// mistakes before sending, and the gateway's answer is what is shown.

export const PURPOSES = [
  {
    value: "homework",
    label: "Homework",
    help: "Learners can see if they got it right as they go.",
  },
  {
    value: "practice",
    label: "Practice",
    help: "Extra practice. Learners can see if they got it right as they go.",
  },
  {
    value: "assessment",
    label: "Test",
    help: "One try per question. Learners see results only after they finish.",
  },
];

export const purposeLabel = (purpose) =>
  PURPOSES.find((p) => p.value === purpose)?.label || "Work";

export const ASSIGNMENT_STATUS = {
  draft: "Not sent",
  assigned: "Open",
  closed: "Closed",
  cancelled: "Cancelled",
};

export const PROGRESS_STATE = {
  not_started: "Not started",
  working: "Working",
  completed: "Finished",
};

// A stable id for one send. Retrying a send reuses it, so a lost answer never
// makes a second assignment.
export const newOperationId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// <input type="datetime-local"> value (local time) → ISO, or null when empty.
export const isoFromLocal = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? new Date(time).toISOString() : undefined;
};

// Returns { problem } in teacher words, or { request } ready to send.
export const assignmentRequest = (form, { operationId, activityId, now = Date.now() }) => {
  if (!PURPOSES.some((p) => p.value === form.purpose)) return { problem: "Choose what kind of work this is." };
  const opensAt = isoFromLocal(form.opensAt);
  const dueAt = isoFromLocal(form.dueAt);
  if (opensAt === undefined || dueAt === undefined) return { problem: "Check the dates." };
  if (dueAt && Date.parse(dueAt) <= now) return { problem: "The due date has already passed." };
  if (opensAt && dueAt && Date.parse(dueAt) <= Date.parse(opensAt)) {
    return { problem: "The due date must be after the start date." };
  }
  const selected = form.audience === "selected_learners";
  if (selected && !form.learnerIds?.length) return { problem: "Choose at least one learner." };
  return {
    request: {
      operationId,
      activityId,
      purpose: form.purpose,
      audience: selected ? "selected_learners" : "classroom",
      ...(selected ? { learnerIds: [...form.learnerIds] } : {}),
      opensAt,
      dueAt,
    },
  };
};

// What the gateway's refusal means for the teacher.
export const assignmentProblem = (error) => {
  const reason = error?.reason;
  const status = error?.response?.status;
  if (!status || error instanceof TypeError) return "You're offline. Your choices are kept; send again when you're connected.";
  if (reason === "bad-dates") return "The due date must be after the start date.";
  if (reason === "learner-not-enrolled") return "One of the chosen learners is no longer in this class. Refresh and choose again.";
  if (reason === "empty-audience") return "Choose at least one learner.";
  if (reason === "activity-not-available") return "This activity can't be set right now.";
  if (reason === "operation-conflict") return "Something changed. Close this and set the work again.";
  if (status === 403) return "You can't set work for this class.";
  return "The work was not sent. Try again.";
};

export const progressCounts = (progress = []) =>
  progress.reduce(
    (counts, row) => ({ ...counts, [row.state]: (counts[row.state] || 0) + 1 }),
    { not_started: 0, working: 0, completed: 0 }
  );
